import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, BarChart, LayoutGrid, LogIn, Loader, Target, Home, LogOut, UserPlus, User, Menu, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

// 1. Header Component (now responsive + mobile friendly)
const Header = ({ userId, currentView, setView = null }) => {
    const navigate = useNavigate();
    const { isAuthenticated, user, logout } = useAuth();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    const isTriageView = currentView === 'triage';

    const handleNavigate = (view) => {
      setIsMobileMenuOpen(false);
      if (setView) {
        setView(view);
      } else {
        navigate('/');
      }
    };

    const handleProtectedNavigate = (view, fromView) => {
      setIsMobileMenuOpen(false);
      if (isAuthenticated) {
        if (setView) {
          setView(view);
        } else {
          navigate('/');
        }
      } else {
        navigate('/login', { state: { fromView } });
      }
    };

    const handleLogout = () => {
      setIsMobileMenuOpen(false);
      logout();
      navigate('/login');
    };

    return (
        <header className="bg-gray-800 text-white shadow-lg sticky top-0 z-50">
            <div className="container mx-auto px-4">
                <div className="flex justify-between items-center h-14">
                    <h1 className="text-xl sm:text-2xl font-bold tracking-tight flex items-center">
                        <Package className="w-6 h-6 mr-2 text-cyan-400" />
                        <span className="whitespace-nowrap hidden md:block">TriageOS: A Circular Asset Platform</span>
                        <span className="whitespace-nowrap md:hidden">TriageOS</span>
                        {isTriageView && (
                            <span className="ml-2 sm:ml-4 px-2 sm:px-3 py-0.5 sm:py-1 bg-yellow-600 text-[10px] sm:text-xs rounded-full font-semibold flex items-center">
                                <Target className="w-3 h-3 mr-1" /> Asset Triage Mode
                            </span>
                        )}
                    </h1>

                    {/* Desktop nav */}
                    <nav className="hidden md:flex items-center space-x-2 lg:space-x-4">
                        <button
                            className={`px-3 py-2 rounded-full text-sm font-medium transition-colors ${
                                currentView === 'landing' ? 'bg-cyan-600 text-white' : 'hover:bg-gray-700'
                            }`}
                            onClick={() => handleNavigate('landing')}
                        >
                            <Home className="w-4 h-4 inline mr-1" /> Home
                        </button>
                        <button
                            className={`px-3 py-2 rounded-full text-sm font-medium transition-colors ${
                                currentView === 'dashboard' ? 'bg-cyan-600 text-white' : 'hover:bg-gray-700'
                            }`}
                            onClick={() => handleProtectedNavigate('dashboard', 'dashboard')}
                        >
                            <BarChart className="w-4 h-4 inline mr-1" /> Dashboard
                        </button>
                        <button
                            className={`px-3 py-2 rounded-full text-sm font-medium transition-colors ${
                                currentView === 'management' ? 'bg-cyan-600 text-white' : 'hover:bg-gray-700'
                            }`}
                            onClick={() => handleProtectedNavigate('management', 'management')}
                        >
                            <LayoutGrid className="w-4 h-4 inline mr-1" /> Asset Manager
                        </button>

                        {isAuthenticated ? (
                          <div className="flex items-center space-x-2">
                            <span className="text-sm text-gray-300 flex items-center">
                              <User className="w-4 h-4 mr-1" />{user?.first_name || user?.email || 'Account'}
                            </span>
                            <button
                              onClick={handleLogout}
                              className="bg-red-600 hover:bg-red-700 text-white py-2 px-3 rounded-full text-sm transition"
                              title="Sign out"
                            >
                              <LogOut className="w-4 h-4 inline mr-1" /> Logout
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => { setIsMobileMenuOpen(false); navigate('/login'); }}
                              className="bg-indigo-600 hover:bg-indigo-700 text-white py-2 px-3 rounded-full text-sm transition flex items-center"
                            >
                              <LogIn className="w-4 h-4 mr-1" /> Sign in
                            </button>
                            <button
                              onClick={() => { setIsMobileMenuOpen(false); navigate('/register'); }}
                              className="bg-green-600 hover:bg-green-700 text-white py-2 px-3 rounded-full text-sm transition flex items-center"
                            >
                              <UserPlus className="w-4 h-4 mr-1" /> Sign up
                            </button>
                          </div>
                        )}
                    </nav>

                    {/* Mobile hamburger */}
                    <div className="md:hidden">
                      <button
                        type="button"
                        aria-label="Toggle menu"
                        aria-controls="mobile-menu"
                        aria-expanded={isMobileMenuOpen}
                        onClick={() => setIsMobileMenuOpen((v) => !v)}
                        className="inline-flex items-center justify-center p-2 rounded-md text-gray-200 hover:text-white hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:ring-offset-2 focus:ring-offset-gray-800"
                      >
                        {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                      </button>
                    </div>
                </div>
            </div>

            {/* Mobile menu panel */}
            {isMobileMenuOpen && (
              <div id="mobile-menu" className="md:hidden border-t border-gray-700 bg-gray-800/95 backdrop-blur-sm">
                <div className="container mx-auto px-4 py-3 space-y-2">
                  <button
                    className={`w-full text-left px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                      currentView === 'landing' ? 'bg-cyan-600 text-white' : 'hover:bg-gray-700'
                    }`}
                    onClick={() => handleNavigate('landing')}
                  >
                    <Home className="w-5 h-5 inline mr-2" /> Home
                  </button>

                  <button
                    className={`w-full text-left px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                      currentView === 'dashboard' ? 'bg-cyan-600 text-white' : 'hover:bg-gray-700'
                    }`}
                    onClick={() => handleProtectedNavigate('dashboard', 'dashboard')}
                  >
                    <BarChart className="w-5 h-5 inline mr-2" /> Dashboard
                  </button>

                  <button
                    className={`w-full text-left px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                      currentView === 'management' ? 'bg-cyan-600 text-white' : 'hover:bg-gray-700'
                    }`}
                    onClick={() => handleProtectedNavigate('management', 'management')}
                  >
                    <LayoutGrid className="w-5 h-5 inline mr-2" /> Asset Manager
                  </button>

                  {isAuthenticated ? (
                    <div className="pt-2 border-t border-gray-700">
                      <div className="px-4 py-2 text-sm text-gray-300 flex items-center">
                        <User className="w-4 h-4 mr-2" /> {user?.first_name || user?.email || 'Account'}
                      </div>
                      <button
                        onClick={handleLogout}
                        className="w-full mt-2 bg-red-600 hover:bg-red-700 text-white py-3 px-4 rounded-lg text-base transition flex items-center justify-center"
                        title="Sign out"
                      >
                        <LogOut className="w-5 h-5 mr-2" /> Logout
                      </button>
                    </div>
                  ) : (
                    <div className="pt-2 border-t border-gray-700 grid grid-cols-2 gap-2">
                      <button
                        onClick={() => { setIsMobileMenuOpen(false); navigate('/login'); }}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white py-3 px-4 rounded-lg text-base transition flex items-center justify-center"
                      >
                        <LogIn className="w-5 h-5 mr-2" /> Sign in
                      </button>
                      <button
                        onClick={() => { setIsMobileMenuOpen(false); navigate('/register'); }}
                        className="bg-green-600 hover:bg-green-700 text-white py-3 px-4 rounded-lg text-base transition flex items-center justify-center"
                      >
                        <UserPlus className="w-5 h-5 mr-2" /> Sign up
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
        </header>
    );
};

export default Header;
